import { useEffect, useState } from "react";
import { Bot, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmSwitch } from "@/components/ConfirmSwitch";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, rupee, istDateTime } from "@/lib/api";

const PHRASE = "I ACCEPT REAL MONEY RISK";
const OPTS = [["arm", "Arm live trading at 09:45 if every safety check passes"],
  ["midday_switch", "12:30 re-check: switch strategies for new entries if the regime flips"],
  ["trail", "Trailing stop: breakeven at +1R, then lock about half the best gain"],
  ["recheck", "Re-check every 30 min (09:45–15:00) and switch to the best strategies for new entries"],
  ["recheck_ai", "Ask the AI Advisor fresh at each re-check (off = reuse the latest advice; cheaper)"]];
const ACTION = { preview: "Preview (no changes)", applied: "Applied", none_fit: "Nothing fits", kept: "Kept morning picks", error: "Error" };

const LogRow = ({ l }) => (
  <div className="border-t border-slate-800 py-2 text-xs" data-testid="autopilot-log-row">
    <div className="flex flex-wrap items-center gap-2">
      <span className="num text-slate-500">{istDateTime(l.ts)}</span>
      <span className="rounded bg-slate-800 px-1.5 py-0.5 uppercase text-slate-300">{l.phase}</span>
      <span className={l.action === "applied" ? "up" : l.action === "preview" ? "text-sky-400" : "down"}>{ACTION[l.action] || l.action}</span>
      {l.regime && <span className="text-slate-400">regime {l.regime}</span>}
      {l.kinds?.length > 0 && <span className="font-semibold text-slate-200">{(l.labels || l.kinds).join(" + ")}</span>}
      {l.basis && <span className="text-slate-500">via {l.basis}</span>}
      {l.need != null && <span className="text-slate-500">margin {rupee(l.need)} of {rupee(l.balance)}</span>}
      <span className={l.armed ? "up" : "text-slate-500"}>{l.armed ? "ARMED" : "not armed"}</span>
    </div>
    {l.blocked?.length > 0 && <div className="mt-1 text-amber-400" data-testid="autopilot-log-blocked">Blocked by: {l.blocked.join(" · ")}</div>}
    {l.error && <div className="mt-1 text-red-400" data-testid="autopilot-log-error">{l.error}</div>}
    {l.notes?.length > 0 && <div className="mt-1 text-slate-500">Skipped: {l.notes.join(" · ")}</div>}
  </div>
);

const ProfitLock = ({ d, busy, save }) => {
  const [v, setV] = useState(String(d.profit_lock));
  useEffect(() => setV(String(d.profit_lock)), [d.profit_lock]);
  const dirty = Number(v) !== d.profit_lock && v !== "" && Number(v) >= 0;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-slate-800 px-3 py-2 text-xs" data-testid="autopilot-profit-lock">
      <span className="font-semibold text-slate-200">Profit lock</span>
      <span className="text-slate-500">exit all live positions when today's P&amp;L reaches ₹</span>
      <Input data-testid="input-profit-lock" type="number" min={0} step={500} value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-24 bg-slate-900 text-xs" />
      {dirty && <Button data-testid="button-save-profit-lock" size="sm" disabled={busy} onClick={() => save({ profit_lock: Number(v) })} className="h-7 text-xs">Save</Button>}
      <label className="flex cursor-pointer items-center gap-2 text-slate-300">
        <Switch data-testid="switch-profit-lock-resume" checked={d.profit_lock_resume} disabled={busy} onCheckedChange={(x) => save({ profit_lock_resume: x })} />
        {d.profit_lock_resume ? "then allow new entries (locks again every further ₹ target)" : "then stop trading for the rest of the day"}
      </label>
      <span className="text-slate-500">{d.profit_lock ? "" : "· off (0)"}</span>
      {d.locked_today && <span className="font-semibold up" data-testid="profit-lock-hit">HIT TODAY</span>}
    </div>
  );
};

export const AutopilotPanel = ({ onChange }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/autopilot").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch, phrase = "") => {
    setBusy(true);
    try { const r = await api.put("/autopilot", { ...d, ...patch, phrase }); setD(r.data); toast.success("Autopilot updated"); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try { await api.post("/autopilot/run"); await load(); onChange?.(); toast.success(d.enabled ? "Autopilot ran — see the log below" : "Preview ready — nothing was changed"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;

  return (
    <div className="panel col-span-12 min-w-0 p-4" data-testid="panel-autopilot">
      <div className="flex flex-wrap items-center gap-3">
        <div className="eyebrow flex items-center gap-2"><Bot className="h-3.5 w-3.5" /> Autopilot · AI picks + margin fit</div>
        <span data-testid="autopilot-status" className={`rounded px-2 py-0.5 text-[11px] font-semibold ${d.enabled ? "bg-emerald-500/15 up" : "bg-slate-800 text-slate-400"}`}>{d.enabled ? "ON" : "OFF"}</span>
        <span className="text-[11px] text-slate-500">Now trading: <b className="text-slate-300" data-testid="autopilot-strategies">{(d.labels || d.strategies).join(", ") || "—"}</b> · {d.armed ? "armed today" : "not armed"}</span>
        <div className="flex w-full gap-2 sm:ml-auto sm:w-auto">
          <Button data-testid="button-autopilot-run" size="sm" variant="outline" disabled={busy} onClick={run} className="h-7 gap-1 border-slate-700 text-xs"><Play size={12} /> {d.enabled ? "Run now" : "Preview picks"}</Button>
          <ConfirmSwitch id="switch-autopilot-enabled" checked={d.enabled} disabled={busy} label={d.enabled ? "Autopilot ON" : "Autopilot OFF"}
            title="Turn on Autopilot with real money?" confirmLabel="Yes, turn on"
            body={d.arm ? "Every weekday at 09:45 IST it will pick strategies and ARM real-money live trading by itself when all safety checks pass. Stops, targets, the profit lock and the ₹4,000 daily stop still apply." : "It will pick strategies by itself; you still arm live trading by hand."}
            onOn={() => save({ enabled: true }, PHRASE)} onOff={() => save({ enabled: false })} />
        </div>
      </div>
      <ProfitLock d={d} busy={busy} save={save} />
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {OPTS.map(([k, label]) => k === "arm" && d.enabled && !d.arm ? (
          <ConfirmSwitch key={k} id="switch-autopilot-arm" checked={false} disabled={busy} label={label} title="Let Autopilot arm real-money trading?"
            body="From the next run it will ARM live trading by itself when every safety check passes." confirmLabel="Yes, allow arming"
            onOn={() => save({ arm: true }, PHRASE)} onOff={() => {}} />
        ) : (
          <label key={k} className="flex cursor-pointer items-start gap-2 text-xs text-slate-300">
            <Switch data-testid={`switch-autopilot-${k}`} checked={!!d[k]} disabled={busy} onCheckedChange={(v) => save({ [k]: v })} />
            <span>{label}</span>
          </label>
        ))}
      </div>
      {d.enabled && d.recheck && (
        <p className="mt-2 text-[11px] text-sky-400" data-testid="autopilot-next-check">
          {d.next_check ? `Next re-check ${istDateTime(d.next_check)} (runs 09:45–15:00 on market days)` : "Re-checks resume at 09:45 IST on the next market day"}
          {d.last_unchanged && <span className="text-slate-500"> · last check {istDateTime(d.last_unchanged.ts)}: no change ({(d.last_unchanged.labels || []).join(" + ") || "—"})</span>}
        </p>
      )}
      <p className="mt-2 text-[11px] text-slate-500">At 09:45 IST it takes the AI Advisor's picks, keeps only strategies that are validated for live, not losing in backtest, don't clash and fit your Dhan margin, and falls back to the Capital Planner's best combo. Existing stops, targets, the 15:15 exit, per-strategy budgets and the ₹4,000 daily stop always apply. Every decision is emailed and logged.</p>
      {Object.keys(d.streaks || {}).length > 0 && (
        <div className="mt-2 text-[11px] text-amber-400" data-testid="autopilot-streaks">
          Benched after 3 losing live days in a row: {Object.values(d.streaks).map((x) => `${x.label} (${x.dates.join(", ")})`).join(" · ")}
        </div>
      )}
      <div className="mt-2" data-testid="autopilot-log">
        {d.log.length ? d.log.map((l) => <LogRow key={l.id} l={l} />) : <p className="text-[11px] text-slate-500" data-testid="autopilot-log-empty">No autopilot runs yet.</p>}
      </div>
    </div>
  );
};
